import { ArrowCircleLeft, PlayArrow } from '@mui/icons-material';
import { Box, IconButton, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import Card from 'components/Card/Card';
import CardContent from 'components/CardContent/CardContent';
import Image from 'components/Image/Image';
import { useGame } from 'contexts/Game/Game';
import { round } from 'helpers';
import { useEffect, useState } from 'react';
import * as setup from 'setup';

export default function ColonyControls() {

    const [disableReserveDecrease, setDisableReserveDecrease] = useState(true);
    const [disableReserveIncrease, setDisableReserveIncrease] = useState(true);
    const game = useGame();

    useEffect(() => {
        // Check unlocks and game state
        const disableAll = !game.unlocks.colonyHealth || !game.unlocks.currentRate || !game.unlocks.reserveLimit || game.finished;

        // Check reserve decrease button
        const newDisableReserveDecrease = disableAll || !game.caloriesReserve;
        setDisableReserveDecrease(newDisableReserveDecrease);

        // Check reserve increase button
        const newDisableReserveIncrease = disableAll || game.caloriesReserve >= setup.caloriesReserveMax;
        setDisableReserveIncrease(newDisableReserveIncrease);
    }, [
        game.caloriesReserve,
        game.finished,
        game.unlocks.colonyHealth,
        game.unlocks.currentRate,
        game.unlocks.reserveLimit,
    ]);

    function handleReserve(increase: boolean) {
        game.sounds.play(increase ? 'portfolio-add' : 'portfolio-remove', 0.5);
        game.changeCaloriesReserve(increase);
    }

    // Get rate
    const rate = round(game.caloriesIncome - game.caloriesCost, 2);
    const rateNegative = rate < 0;

    // Get health and reserve (inversed) percentages
    const healthPercentage = 100 - Math.max(0, game.calories / setup.caloriesReserveMax * 100);
    const reservePercentage = game.unlocks.reserveLimit ? (100 - Math.max(0, game.caloriesReserve / setup.caloriesReserveMax * 100)) : 50;

    return <Card id="colonyControls" info={game.unlocks.colonyHealth ? 'colonyInfo' : null}>
        <Box display="flex">
            <Box display="flex" flex={1} justifyContent="center">
                <Box display="grid" gridTemplateColumns="1fr 40px">
                    <Box />
                    <CardContent locked={!game.unlocks.colonyHealth || !game.unlocks.reserveLimit}>
                        <Box textAlign="center">
                            <IconButton aria-label="increase minimum calories reserve" disabled={disableReserveIncrease} onClick={() => handleReserve(true)}>
                                <ArrowCircleLeft sx={{ transform: 'rotate(90deg)' }} />
                            </IconButton>
                        </Box>
                    </CardContent>
                    <CardContent locked={!game.unlocks.colonyHealth || !game.unlocks.reserveLimit}>
                        <Box position="relative" paddingRight={2} top={`${reservePercentage}%`}>
                            <Box bgcolor={game.unlocks.reserveLimit ? 'primary.main' : 'action.disabled'} borderRadius={4} color="white" paddingX={2} paddingY={1} sx={{ transform: 'translateY(-50%)' }} textAlign="center">
                                <Typography variant="roboto414">
                                    Reserve
                                    <br />
                                    Limit:
                                    <br />
                                    <b>{game.caloriesReserve} cal</b>
                                </Typography>
                            </Box>
                            <Box color={game.unlocks.reserveLimit ? 'primary.main' : 'action.disabled'} position="absolute" right={0} top={-12}>
                                <PlayArrow color="inherit" />
                            </Box>
                        </Box>
                    </CardContent>
                    <CardContent locked={!game.unlocks.colonyHealth}>
                        <Box bgcolor="secondary.light" border={3} borderColor="grey.700" borderRadius={3} height={180} overflow="hidden" position="relative" width={40}>
                            <Box bgcolor="white" height={`${healthPercentage}%`} position="relative" />
                            {game.unlocks.reserveLimit && (
                                <Box bgcolor="primary.main" height={reservePercentage ? 2 : 0} left={0} position="absolute" right={0} top={`${reservePercentage}%`} zIndex={1} />
                            )}
                        </Box>
                    </CardContent>
                    <Box />
                    <CardContent locked={!game.unlocks.colonyHealth || !game.unlocks.reserveLimit}>
                        <Box textAlign="center">
                            <IconButton aria-label="decrease minimum calories reserve" disabled={disableReserveDecrease} onClick={() => handleReserve(false)}>
                                <ArrowCircleLeft sx={{ transform: 'rotate(-90deg)' }} />
                            </IconButton>
                        </Box>
                    </CardContent>
                </Box>
            </Box>
            <Box display="flex" flex={1} flexDirection="column" gap={1} justifyContent="center">
                <CardContent locked={!game.unlocks.colonyHealth || !game.unlocks.currentRate}>
                    <Box fontSize={80} lineHeight={0} marginBottom={3} textAlign="center">
                        {rateNegative ? (
                            <Image alt="" aria-hidden src="/assets/arrow-down.svg" />
                        ) : (
                            <Image alt="" aria-hidden src="/assets/arrow-up.svg" />
                        )}
                    </Box>
                </CardContent>
                <CardContent locked={!game.unlocks.colonyHealth || !game.unlocks.currentRate}>
                    <Box alignItems="center" display="flex" gap={1} justifyContent="center">
                        <Typography variant="roboto418">
                            <Box component="span" sx={visuallyHidden}>
                                current consumption:
                            </Box>
                            <Box aria-hidden component="span">
                                {rateNegative ? undefined : '+'}
                            </Box>
                            {rate}
                            <Box component="span" sx={visuallyHidden}>
                                calories per second
                            </Box>
                        </Typography>
                        <Typography aria-hidden variant="roboto414">
                            cal / sec
                        </Typography>
                    </Box>
                </CardContent>
                <CardContent locked={!game.unlocks.colonyHealth}>
                    <Box alignItems="center" display="flex" gap={1} justifyContent="center">
                        <Typography variant="roboto728">
                            <Box component="span" sx={visuallyHidden}>
                                current calories:
                            </Box>
                            {game.calories}
                        </Typography>
                        <Typography aria-hidden variant="roboto718">
                            Calories
                        </Typography>
                    </Box>
                </CardContent>
            </Box>
        </Box>
    </Card>;

}